import { Box, Grid, Stack, Step, StepButton, Stepper } from '@mui/material';
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcBarTitle, MprcButton, MprcContainer, MprcFormBarHeader, MprcPrompt, MprcRequestErrorDialog, MprcRequestIndicator } from 'components';
import { ButtonBack, ButtonSave } from 'components/buttons';
import { BroadcastMessageType, ExecuteUserOperationOptions, UserInfoDtoPaginatedList, UserOperationBroadcastMessageProps, UserOperationResults, UserOperationType } from 'generated/CoreApi_v1';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BroadcastMessageStepProps } from 'views/foundation/broadcastMessage/Home';

////////////////////////////////////////////////////////////////////////////

interface IStep {
  name: string;
  enabled: boolean;
  visible: boolean;
  render: React.FC<BroadcastMessageStepProps>;
}

interface IEditStepsProps {
  steps: IStep[];
}

type BroadcastMessageContextType = {
  message: UserOperationBroadcastMessageProps;
  setMessage: React.Dispatch<React.SetStateAction<UserOperationBroadcastMessageProps>>;
  selectedUsers?: UserInfoDtoPaginatedList;
  setSelectedUsers: React.Dispatch<React.SetStateAction<UserInfoDtoPaginatedList | undefined>>;
  result: UserOperationResults | null;
};

const BroadcastMessageContext = React.createContext<BroadcastMessageContextType>(undefined!);

////////////////////////////////////////////////////////////////////////////////

export const useBroadcastMessageContext = () => React.useContext(BroadcastMessageContext);


export const StepManagement: React.FC<IEditStepsProps> = (props) => {

  const api = useCoreApi();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [saving, setSaving] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [canSave, setCanSave] = useState<boolean>(false);
  const [viewError, setViewError] = useState<any>(null);
  const [steps, setSteps] = useState<IStep[]>(props.steps);
  const [showLostChange, setShowLostChange] = useState<boolean>(false);
  const [message, setMessage] = useState<UserOperationBroadcastMessageProps>({
    type: BroadcastMessageType.CustomMessage,
    subject: null,
    body: null,
    allActiveUsers: false
  });
  const [result, setResult] = useState<UserOperationResults | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<UserInfoDtoPaginatedList | undefined>();

  const numberOfSteps = useRef<number>(3);
  const maxStep = useRef<number>(0);

  ////////////////////////////////////////////////////////////////////////////

  ///
  const goBack = () => {
    if (currentStep == 1) {
      setCurrentStep(state => state > 0 ? state - 1 : state);
    } else {
      navigate(-1);
    }
  };

  ///
  const sendMessages = () => {
    setSaving(true);
    api.v1.userOperation.executeUserOperation({
      operation: UserOperationType.BroadcastMessage,
      userIds: (selectedUsers?.items ?? []).map(item => item.userId),
      options: {
        broadcastMessage: message
      } as ExecuteUserOperationOptions
    }).then(response => {
      setResult(response.data);
      setCurrentStep(2);
    }).catch(e => setViewError(e))
      .finally(() => setSaving(false));
  };

  const newMessage = () => {
    setCurrentStep(0);
    setMessage({
      type: BroadcastMessageType.CustomMessage,
      subject: null,
      body: null,
      allActiveUsers: false
    });
    setSelectedUsers(undefined);
    setResult(null);
    setViewError(null);
  };

  ///
  const getHeaderItems = () => {
    const actions: React.ReactNode[] = [];
    const rightOptions: React.ReactNode[] = [];

    actions.push(
      <ButtonBack
        key="btnBack"
        rounded
        variant="outlined"
        onClick={goBack}
      />
    );

    actions.push(<MprcBarTitle>{t("App.Menu.Core.BroadcastMessage", { ns: "shared" })}</MprcBarTitle>);

    if (currentStep == 0) {
      rightOptions.push(
        <MprcButton
          text={t("BroadcastMessage.StepEdit.NextStepButtonText")}
          title={t("BroadcastMessage.StepEdit.NextStepButtonText")}
          key="nextStep"
          color="primary"
          variant="text"
          disabled={!canSave}
          onClick={() => setCurrentStep(1)}
        />
      );
    }
    else if (currentStep == 1) {
      rightOptions.push(
        <ButtonSave
          text={t("BroadcastMessage.StepDestination.SendButtonText")}
          title={t("BroadcastMessage.StepDestination.SendButtonText")}
          key="send"
          variant="text"
          disabled={!canSave}
          onClick={sendMessages}
        />
      );
    }
    else if (currentStep == 2) {
      rightOptions.push(
        <MprcButton
          text={t("BroadcastMessage.StepResult.NewMessageButtonText")}
          title={t("BroadcastMessage.StepResult.NewMessageButtonText")}
          key="nextStep"
          color="primary"
          variant="text"
          onClick={newMessage}
        />
      );
    }


    actions.push([<Stack key="editContentHeaderRightOptions" direction="row" gap={1} alignItems="center">{rightOptions}</Stack>]);

    return actions;
  };

  ////////////////////////////////////////////////////////////////////////////
  /// EFFECT's
  ////////////////////////////////////////////////////////////////////////////

  ///
  useEffect(() => {
    numberOfSteps.current = 3;
    maxStep.current = numberOfSteps.current - 1;
    setSteps((state) =>
      state.map(
        (s, index) => (
          {
            name: s.name,
            enabled: index <= currentStep && currentStep < 2,
            visible: index < 3,
            render: s.render
          }
        )
      )
    );
  }, [currentStep]);



  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  const events: BroadcastMessageStepProps = {
    canSave, setCanSave,
    goNextStep: () => setCurrentStep(state => state < 3 ? state + 1 : state),
    goFirstStep: () => setCurrentStep(0),
  };

  const RenderStep = steps[currentStep].render;

  const contextValue = useMemo(() => ({
    message,
    setMessage,
    result,
    selectedUsers,
    setSelectedUsers
  }), [message, result, selectedUsers]);

  return <BroadcastMessageContext.Provider value={contextValue}>
    <Box height="100%">

      <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />
      <MprcRequestIndicator visible={saving} type={"post"} />
      <Grid container direction="column" height="100%">
        <Grid width="100%">
          <MprcContainer>
            <MprcFormBarHeader actions={getHeaderItems()} />
            <MprcPrompt
              open={showLostChange}
              onCancel={() => setShowLostChange(false)}
              onConfirm={goBack} />
          </MprcContainer>
          <section id="steps">
            <MprcContainer>
              <Stepper
                activeStep={currentStep}
                orientation="horizontal"
                nonLinear
              >
                {
                  steps
                    .filter(p => p.visible)
                    .map((s, index) =>
                      <Step
                        key={index}
                        disabled={!steps[index].enabled}
                        onClick={
                          () => steps[index].enabled && setCurrentStep(index)
                        }
                      >
                        <StepButton
                          sx={(theme) => ({
                            textTransform: "uppercase",
                            "& .MuiStepLabel-labelContainer": {
                              color: steps[index].enabled ? theme.palette.common.black : undefined,
                            },
                            "& svg": {
                              color: index < currentStep ? theme.palette.info.main : undefined,
                            }
                          })}
                          className="Mui-active"
                        >
                          {s.name}
                        </StepButton>
                      </Step>
                    )
                }
              </Stepper>
            </MprcContainer>
          </section>
        </Grid>

        <Grid size="grow" overflow="auto" className={AppGlobalClassKeys.thinScrollbar} width="100%">
          <Grid container direction="column" height="100%" width="100%">
            <Grid size="grow" width="100%">
              <RenderStep {...events} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  </BroadcastMessageContext.Provider>;
};
